import {
  useEffect,
  useMemo,
  useState,
  useCallback,
  type ReactElement,
} from "react";

import clsx from "clsx";
import { formatDuration, intervalToDuration } from "date-fns";
import { capitalize, compact, orderBy } from "lodash";
import moment from "moment";

import amazonFilledIcon from "@assets/platform-icons/amazon-filled-icon.svg";
import applovinFilledIcon from "@assets/platform-icons/applovin-filled-icon.svg";
import blisspointFilledIcon from "@assets/platform-icons/blisspoint-filled-icon.svg";
import facebookFilledIcon from "@assets/platform-icons/facebook-filled-icon.svg";
import genericFilledIcon from "@assets/platform-icons/generic-filled-icon.svg";
import googleFilledIcon from "@assets/platform-icons/google-filled-icon.svg";
import mediamathFilledIcon from "@assets/platform-icons/mediamath-filled-icon.svg";
import microsoftFilledIcon from "@assets/platform-icons/microsoft-filled-icon.svg";
import mntnFilledIcon from "@assets/platform-icons/mntn-filled-icon.svg";
import northbeamFilledIcon from "@assets/platform-icons/northbeam-filled-icon.svg";
import pinterestFilledIcon from "@assets/platform-icons/pinterest-filled-icon.svg";
import s2sFilledIcon from "@assets/platform-icons/s2s-filled-icon.svg";
import shopifyFilledIcon from "@assets/platform-icons/shopify-filled-icon.svg";
import snapchatFilledIcon from "@assets/platform-icons/snapchat-filled-icon.svg";
import tiktokFilledIcon from "@assets/platform-icons/tiktok-filled-icon.svg";
import veFilledIcon from "@assets/platform-icons/ve-filled-icon.svg";
import { ModalPanel } from "@components/v2";
import { IconButton } from "@components/v2/buttons/icon-button";
import { useNorthbeamQuery } from "@hooks/http";
import { useDesignElementManager } from "@hooks/use-design-element-manager";
import {
  DATA_REFRESH_TIME_QUERY,
  type DataRefreshTime,
  NAVBAR_QUERY,
  type Navbar as NavbarQuery,
} from "@north-beam/nb-client-side";
import { Modal } from "@shared/modal";

import { ProcessingChart } from "./processing-chart";
import { ProcessingStatus } from "./processing-status";

const REFRESH_TIME_MS = 60_000;

const parseConnectionIssues = (data: NavbarQuery | undefined) => {
  try {
    return (
      data?.me?.accountMaintenanceItems?.map(
        (item) => JSON.parse(item.id).id,
      ) ?? []
    );
  } catch {
    return [];
  }
};

const KEY_CONNECTION_MAP: { [key: string]: string[] } = {
  adwords: ["integration_google_adwords"],
  microsoft: ["integration_microsoft"],
  fb: ["integration_facebook_ads"],
  snapchat: ["integration_snapchat"],
  pinterest: ["integration_pinterest"],
  tiktok: ["integration_tiktok"],
  "bliss-point-media": ["bliss-point-media"],
  amazon: ["integration_amazon", "integration_amazon_seller"],
  "northbeam/pixel": ["Northbeam pixel"],
  shopify: ["integration_shopify"],
  amazon_ads: ["integration_amazon", "integration_amazon_seller"],
};

const KEY_ICON_MAP: { [key: string]: string } = {
  adwords: googleFilledIcon,
  microsoft: microsoftFilledIcon,
  fb: facebookFilledIcon,
  snapchat: snapchatFilledIcon,
  pinterest: pinterestFilledIcon,
  tiktok: tiktokFilledIcon,
  ve: veFilledIcon,
  dv360_ve: veFilledIcon,
  bliss_point_media: blisspointFilledIcon,
  bliss_point_media_linear: blisspointFilledIcon,
  bliss_point_media_audio: blisspointFilledIcon,
  amazon_marketplace_aggregated: amazonFilledIcon,
  applovin: applovinFilledIcon,
  mediamath: mediamathFilledIcon,
  spend_impressions__mntn: mntnFilledIcon,
  "northbeam/pixel": northbeamFilledIcon,
  "northbeam/custom-orders": northbeamFilledIcon,
  "northbeam/custom-spend": northbeamFilledIcon,
  "northbeam/enquire-labs": northbeamFilledIcon,
  "northbeam/influencer-spend": northbeamFilledIcon,
  shopify: shopifyFilledIcon,

  orders_custom: s2sFilledIcon,
  orders_dummy: genericFilledIcon,
  amazon: amazonFilledIcon,
  amazon_ads: amazonFilledIcon,
};

const KEY_TITLE_MAP = {
  // REVENUE
  "northbeam/pixel": "Northbeam Pixel",
  shopify: "Shopify",
  orders_dummy: "Dummy",
  amazon: "Amazon",

  // SPEND
  adwords: "Google Ads",
  microsoft: "Microsoft Ads",
  fb: "Facebook Ads",
  snapchat: "Snapchat Ads",
  pinterest: "Pinterest Ads",
  tiktok: "TikTok Ads",
  ve: "VE",
  dv360_ve: "DV360 VE",
  "bliss-point-media": "Bliss Point Media",
  amazon_ads: "Amazon Marketplace Aggregated",

  mediamath: "Mediamath",
  mntn: "MNTN",
  criteo: "Criteo",

  tatari: "Tatari",
  adelphic: "Adelphic",
  adnet: "Adnet",
  "attentive-mobile": "Attentive Mobile",
  hivewyre: "Hivewyre",
  klaviyo: "Klaviyo",
  applovin: "AppLovin",

  "northbeam/custom-orders": "Orders API (S2S, WooCommerce and Magento)",
  "northbeam/custom-spend": "Northbeam Spend Sheet/API",
  "northbeam/enquire-labs": "Northbeam Enquire Labs",
  "northbeam/influencer-spend": "Northbeam Influencers",

  tradedesk: "Tradedesk",
  zeta: "Zeta",

  "northbeam/partnership-instagram": "Partnership Instagram",
  "northbeam/partnership-podcast": "Partnership Podcast",
  "northbeam/partnership-youtube": "Partnership Youtube",
  "northbeam/partnership-tiktok": "Partnership TikTok",
};

function toHumanReadable(s: string) {
  return s
    .split(/,|-|_|\//)
    .map(capitalize)
    .join(" ");
}

function humanReadablePlatform(platform: string) {
  return toHumanReadable(platform);
}

function fromThenToNow(from: Date) {
  const duration = intervalToDuration({
    start: from,
    end: new Date(),
  });

  const formattedDuration = formatDuration(duration, {
    format: ["days", "hours", "minutes"],
    delimiter: " and ",
  }).replace("minutes", "mins");

  if (!formattedDuration) {
    return "Now";
  }

  return `${formattedDuration} ago`;
}

type HumanReadableBlessings = {
  key: string;
  keyTitleMap: Record<string, string>;
  displayName: string;
  data: {
    title: string;
    time: string;
    needsReconnect: boolean;
    relativeLastUpdate: string;
    icon?: ReactElement;
    iconString: string;
  }[];
}[];

export const ProcessingModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { designElementManager } = useDesignElementManager();

  const {
    data: refreshData,
    startPolling,
    stopPolling,
  } = useNorthbeamQuery<DataRefreshTime>(DATA_REFRESH_TIME_QUERY, {
    doNotRaiseErrorOnFailure: true,
  });

  const { data: navbarData } = useNorthbeamQuery<NavbarQuery>(NAVBAR_QUERY, {
    pollInterval: 60_000,
    doNotRaiseErrorOnFailure: true,
  });

  const brokenConnections = useMemo(
    () => parseConnectionIssues(navbarData),
    [navbarData],
  );

  useEffect(() => {
    startPolling(REFRESH_TIME_MS);
    return () => stopPolling();
  }, [startPolling, stopPolling]);

  const { dataProcessedAt, nextProcessedAt, timezone, dataBlessingTimes } =
    useMemo(() => {
      if (refreshData) {
        return {
          ...refreshData.me,
          dataBlessingTimes: refreshData.me.dataBlessingTimesV2,
        };
      }
      return {
        dataBlessingTimes: [],
        dataProcessedAt: "",
        nextProcessedAt: "",
        processingPerDay: 0,
        timezone: "",
      };
    }, [refreshData]);

  const previousProcessedAt = useMemo(() => {
    const processedAtMoment = moment(dataProcessedAt);
    const hoursFromStartOfDay = processedAtMoment.hour();
    return processedAtMoment
      .subtract(48 + hoursFromStartOfDay, "hours")
      .startOf("hour")
      .toISOString();
  }, [dataProcessedAt]);

  const createDataBlessingDataReadable = useCallback(() => {
    const dataBlessingsOrderedByTimestamp = orderBy(
      dataBlessingTimes,
      "timestamp",
    );
    const humanReadableDataBlessing: HumanReadableBlessings = [
      { key: "spend", displayName: "Spend", keyTitleMap: KEY_TITLE_MAP },
      {
        key: "revenue",
        displayName: "Revenue",
        keyTitleMap: KEY_TITLE_MAP,
      },
    ].map(({ key, displayName, keyTitleMap }) => {
      const filteredByCategory = dataBlessingsOrderedByTimestamp.filter(
        (i) => i.type === key,
      );
      const data: HumanReadableBlessings[number]["data"] = compact(
        filteredByCategory.map((blessing) => {
          let maxTime = moment("INVALID");
          const iconString = KEY_ICON_MAP[blessing.platform];
          const icon: ReactElement = designElementManager
            .getDesignElement(blessing.platform.split("/")?.[0] ?? "")
            .getComponent({
              size: "25px",
            });

          const dataBlessingTime = moment(blessing.timestamp);
          const time = dataBlessingTime
            .tz(timezone)
            .format("MMM D [@] HH:mm z");
          const relativeLastUpdate = fromThenToNow(dataBlessingTime.toDate());
          const connectionKey: string[] = KEY_CONNECTION_MAP[blessing.platform];
          const brokenConnection = connectionKey?.filter((connection) =>
            brokenConnections.includes(connection),
          );

          const needsReconnect =
            brokenConnection && brokenConnection.length > 0;

          const title: string =
            KEY_TITLE_MAP[blessing.platform as keyof typeof KEY_TITLE_MAP] ||
            humanReadablePlatform(blessing.platform);

          if (dataBlessingTime.isValid()) {
            if (!maxTime.isValid() || maxTime < dataBlessingTime) {
              maxTime = dataBlessingTime;
            }
            return {
              title,
              time,
              relativeLastUpdate,
              needsReconnect,
              icon,
              iconString,
            };
          } else {
            return {
              title,
              time: "",
              relativeLastUpdate: "",
              needsReconnect,
              icon,
              iconString,
            };
          }
        }),
      );
      return {
        key,
        keyTitleMap,
        displayName,
        data,
      };
    });

    return humanReadableDataBlessing;
  }, [dataBlessingTimes, timezone, designElementManager, brokenConnections]);

  const dataBlessingData = useMemo(() => {
    const rawData = createDataBlessingDataReadable();
    return rawData.flatMap((category) => category.data);
  }, [createDataBlessingDataReadable]);

  const hasReconnectItems = useMemo(() => {
    return dataBlessingData.some((item) => item.needsReconnect);
  }, [dataBlessingData]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        hideCloseButton
        contentClassName="max-w-[1220px] h-auto bg-v2-glass-inverse"
        variant="v2"
      >
        <ModalPanel className="w-full h-full relative flex flex-col gap-4 p-6">
          <div className="flex items-start justify-between">
            <ProcessingStatus
              dataProcessedAt={dataProcessedAt}
              nextProcessedAt={nextProcessedAt}
              timezone={timezone}
              refreshTimeMs={REFRESH_TIME_MS}
            />
            <IconButton
              variant="tertiary"
              onClick={onClose}
              className="text-cosmic text-base -translate-y-1/3"
            >
              <i className="fa-regular fa-xmark"></i>
            </IconButton>
          </div>
          <div className="flex-grow flex flex-col">
            <ProcessingChart
              className="flex-grow h-[442px]"
              previousProcessedAt={previousProcessedAt}
            />
            {/* <div className="flex items-center justify-between gap-3">
              <TertiaryButton
                // eslint-disable-next-line max-len
                className="border-v2-action-primary text-sm
                 border rounded-[24px] bg-v2-white/90 shadow-[inset_0px_-5px_10px_0px_rgba(209,212,219,0.15),
                 inset_0px_5px_10px_0px_rgba(255,255,255,0),
                0px_-2px_2px_0px_rgba(255,255,255,0),0px_2px_2px_0px_rgba(0,0,0,0.1)] backdrop-blur-[1px]"
              >
                More Frequent Updates
                <i className="fa-regular fa-info-circle ml-2.5"></i>
              </TertiaryButton>
              <SecondaryButton
                className={clsx(
                  "border-v2-action-positive hover:border-v2-action-positive-hover",
                  "text-sm text-v2-action-positive hover:text-v2-action-positive-hover",
                  "border rounded-[24px] bg-v2-white/90",
                  "shadow-[inset_0px_-5px_10px_0px_rgba(209,212,219,0.15),
                  inset_0px_5px_10px_0px_rgba(255,255,255,0),0px_-2px_2px_0px_rgba(255,255,255,0),0px_2px_2px_0px_rgba(0,0,0,0.1)]",
                  "backdrop-blur-[1px]"
                )}
              >
                Update Now
                <i className="fa-regular fa-info-circle ml-2.5"></i>
              </SecondaryButton>
            </div> */}
          </div>
          {hasReconnectItems && (
            <div className="mt-4 border-v2-tertiary">
              <div className="flex flex-col rounded-lg border border-v2-tertiary overflow-hidden">
                <div className="flex text-v2-primary font-tt-commons-pro text-xs font-bold leading-5 tracking-[0.24px] uppercase">
                  <div className="flex-grow border-r border-b border-v2-tertiary p-2">
                    Platforms
                  </div>
                  <div className="w-[190px] border-b border-v2-tertiary p-2">
                    Last Update
                  </div>
                </div>
                {dataBlessingData.map(
                  (item) =>
                    item.needsReconnect && (
                      <div key={item.title} className="flex text-sm">
                        <div
                          className={clsx(
                            "flex-grow border-r border-b border-v2-tertiary py-2 px-4 flex items-center gap-2 justify-between",
                            {
                              "border-b border-v2-tertiary":
                                !item.needsReconnect &&
                                dataBlessingData
                                  .filter((i) => !i.needsReconnect)
                                  .indexOf(item) !==
                                  dataBlessingData.filter(
                                    (i) => !i.needsReconnect,
                                  ).length -
                                    1,
                            },
                          )}
                        >
                          <div className="flex items-center gap-2">
                            {item.iconString ? (
                              <img
                                src={item.iconString}
                                alt={`${item.title} logo`}
                              />
                            ) : (
                              item.icon
                            )}
                            <span className="text-v2-primary text-sm font-medium leading-5 tracking-[0.28px]">
                              {item.title}
                            </span>
                          </div>
                          <span className="text-bad">
                            <i
                              className={clsx(
                                "fa-sharp fa-regular fa-sharp",
                                "fa-circle-exclamation",
                              )}
                            />
                          </span>
                        </div>
                        <div
                          className={clsx(
                            "w-[190px] p-2 text-v2-primary font-mono text-xs",
                            {
                              "border-b border-v2-tertiary":
                                dataBlessingData
                                  .filter((i) => !i.needsReconnect)
                                  .indexOf(item) !==
                                dataBlessingData.filter(
                                  (i) => !i.needsReconnect,
                                ).length -
                                  1,
                            },
                          )}
                        >
                          {item.time}
                        </div>
                      </div>
                    ),
                )}
              </div>
            </div>
          )}
        </ModalPanel>
      </Modal>
    </>
  );
};
